import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { commonClassName, commonHeaderClass } from './PromoScorecard';
import { getAdditionalDataOnCopyClick, getConfig, parseAttributeType } from 'utils/helperMethods';
import { UserContext } from 'contexts/ConfigContext';
import { CustomTable } from 'components/molecules/GrowthAnalysis/GrowthAnalysisTable';
import { FilterContextData } from 'contexts/FilterContext';
import _ from 'lodash';
import { CustomSelect } from 'components/template/BumpReport/MainSection';
import DownSvg from 'assets/svg/Down';
import PricePointSection from './PricePointSection';
import { height, subHeaderClass } from 'components/template/GrowthAnalysis/ExpandedView';
import { useFetchPricePointGraph } from 'hooks/pricePoint/useFetchGraphData';
import TileButton from 'components/atoms/TileButton';
import PricePointMultiSelectionDropdownV2 from 'components/molecules/PricePointMultiSelectDropdown';
import { useFetchDropdownData } from 'hooks/pricePoint/useFetchDropdownData';
import { defaultApplyFilter } from './GrowthAnalysis';
import { useApplyFilterMutation } from 'hooks/general/useApplyFilterMutation';
import { useFetchTableData } from 'hooks/pricePoint/useFetchTableData';
import { getPricePointColumns, getPricePointDrilldownColumns } from 'utils/getPricePointTableColumns';
import { useFetchDrilldownTableData } from 'hooks/pricePoint/useFetchDrilldownTableData';
import styled from '@emotion/styled';
import { Table } from 'antd';
import { colorConstants } from 'config/colorConstants';
import tw from 'twin.macro';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import { pricePointDrilldownDownload, pricePointTableDownload } from 'parser/graphToExcelDownloadParser';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import { useFetchCustomRangeData } from 'hooks/pricePoint/useFetchCustomRangeData';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    PRICE_POINT_ATTRIBUTE_NAME_DROPDOWN_CHANGED,
    PRICE_POINT_ATTRIBUTE_TYPE_DROPDOWN_CHANGED,
    PRICE_POINT_PRICE_TYPE_DROPDOWN_CHANGED,
    PRICE_POINT_TAB_CHANGED,
    PRICE_POINT_VALUE_DROPDOWN_CHANGED,
} from 'utils/mixpanelEventNames';

const pricePointMainHeader = 'Price Point';

const priceLadderTableId = 'price-point-table-wrapper';

export const absTileData = [
    { key: 'cy', label: 'CY' },
    { key: 'ya', label: 'YA' },
    { key: 'absChange', label: 'Change' },
];

export const CustomTableExpanded = styled(Table)`
    .ant-table-sticky-scroll {
        position: static;
    }
    .ant-table-thead > tr > th {
        background: ${colorConstants.grey};
        padding: 0px;
        height: 68px;
    }
    .ant-table-header col:not(:last-child),
    .ant-table-body col {
        min-width: 30px;
    }
    .ant-table-thead
        > tr
        > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not(
            [colspan]
        ):before {
        background: none;
    }
    .ant-table-column-sorter {
        position: relative;
        left: -10px;
    }
    .ant-table-column-sorter-down.active,
    .ant-table-column-sorter-up.active {
        ${tw`text-purpleFont`};
    }
    border: ${'1px solid #DBDFE9'};
`;

const PricePoint = () => {
    const [attributeTypesList, setAttributeTypesList] = useState<any[]>([]);
    const [selectPPG, setSelectPPG] = useState<any>(null);

    const { appConfig } = useContext(UserContext);
    const pricePointConfig = getConfig('pricePoint', appConfig);
    const globalFilterConfig = getConfig('globalFilter', appConfig);
    const commonConfig = getConfig('commonConfig', appConfig);
    const { decimalForPercentage, decimalForNonPercentage } = commonConfig;
    const [applyFilterTempLoading, setApplyFilterTempLoading] = useState(false);
    const {
        allDimAndAttributeTypeList,
        allCheckedAttribute,
        idForRestOfComponent,
        timeSelectionLabel,
        timePeriodSelection,
        currentFrequency,
    } = useContext(FilterContextData);

    const tableId = `landscape-table-wrapper`;
    const {
        priceTypeOptions,
        dimensionConfig: { excludedDimensions, defaultDimension },
    } = pricePointConfig;
    const [selectedPriceTypeDropdown, setSelectedPriceTypeDropdown] = useState<any>(priceTypeOptions?.[0]);
    const [expandedIndex, setExpandedIndex] = useState<null | number>(null);
    const [subTileState, setSubTileState] = useState(pricePointConfig.subTileData[0]);
    const [yAxisDropdownData, setYAxisDropdownData] = useState(pricePointConfig.yAxisDropdownData[0]);
    const [selectedGraphItem, setSelectedGraphItem] = useState<any>(null);
    const [batchSize, setBatchSize] = useState(1);
    const [priceRange, setPriceRange] = useState<null | [number, number]>(null);
    const [defaultPriceRange, setDefaultPriceRange] = useState<any>(null);
    const [customThreshold, setCustomThreshold] = useState([...pricePointConfig.customThresholdData]);
    const [search, setSearch] = useState('');
    const [customThresholdSwitch, setCustomThresholdSwitch] = useState(false);

    const dropdownFilterRef = useRef<any>();
    const tableCopyRef = useRef<any>();
    const expandedTableCopyRef = useRef<any>();
    const dropdownOffsetRef = useRef(0);
    const drilldownOffsetRef = useRef(0);

    const {
        loading: customRangeLoading,
        error: customRangeError,
        data: customRangeData,
        fetchCustomRangeData,
    } = useFetchCustomRangeData({
        setDefaultPriceRange,
        setPriceRange,
        subTileState,
    });

    const {
        data: dropdownData,
        loading: dropdownLoading,
        error: dropdownError,
        count: dropdownCount,
        fetchDropdownData,
    } = useFetchDropdownData();

    const {
        data: graphData,
        error: graphError,
        loading: graphLoading,
        fetchPricePointGraph,
        clearGraphData,
        throwError: throwGraphError,
    } = useFetchPricePointGraph();

    const {
        loading: pricePointTableLoading,
        error: pricePointTableError,
        data: pricePointTableData,
        fetchPricePointTable,
        clearData: clearTableData,
        throwError: throwTableError,
    } = useFetchTableData();

    const {
        data: drilldownTableData,
        loading: drilldownTableDataLoading,
        error: drilldownTableDataError,
        fetchPricePointDrilldownTable,
    } = useFetchDrilldownTableData();

    const {
        data: applyFilterData,
        loading: applyFilterLoading,
        error: applyFilterError,
        applyFilterMutate,
    } = useApplyFilterMutation({ onComplete: onApplyFilterComplete, onError: onApplyFilterError });

    function onCheckboxChange(data: any) {
        setExpandedIndex(null);
        setSelectedGraphItem(null);
        prepareApplyFilterVariableAndExecute(data);
        //mixpanel event
        trackEventMixpanel(PRICE_POINT_ATTRIBUTE_NAME_DROPDOWN_CHANGED);
        // if (data && Object.keys(data)?.length > 0) {
        //     prepareApplyFilterVariableAndExecute(data);
        // } else {
        //     throwGraphError('No Data');
        //     throwTableError('No Data');
        // }
    }

    function onApplyFilterComplete(payload: any) {
        const updatedFilter = getIntersectionOnApplyFilter(idForRestOfComponent, payload);
        dropdownFilterRef.current = updatedFilter;
        fetchRangeData();
        // fetchGraphData();
        // fetchPricePointTableData();
    }

    function onApplyFilterError() {}

    //call apply filter
    function prepareApplyFilterVariableAndExecute(unCheckedOptions: any = {}) {
        const tempApplyFilterVariable: any = _.cloneDeep(defaultApplyFilter);
        tempApplyFilterVariable.jsonstring_dim1.exclusion = [];
        tempApplyFilterVariable.jsonstring_dim2.exclusion = [];
        Object.keys(unCheckedOptions)?.map((item) => {
            const attributeItem = unCheckedOptions[item];
            if (attributeItem?.dimension === 'dim1') {
                tempApplyFilterVariable.jsonstring_dim1.exclusion.push({
                    ...attributeItem,
                    isChecked: false,
                });
            } else {
                tempApplyFilterVariable.jsonstring_dim2.exclusion.push({
                    ...attributeItem,
                    isChecked: false,
                });
            }
        });
        if (!tempApplyFilterVariable.jsonstring_dim1.exclusion?.length)
            delete tempApplyFilterVariable.jsonstring_dim1.exclusion;
        if (!tempApplyFilterVariable.jsonstring_dim2.exclusion?.length)
            delete tempApplyFilterVariable.jsonstring_dim2.exclusion;
        tempApplyFilterVariable.jsonstring_dim1 = JSON.stringify(tempApplyFilterVariable.jsonstring_dim1);
        tempApplyFilterVariable.jsonstring_dim2 = JSON.stringify(tempApplyFilterVariable.jsonstring_dim2);
        setApplyFilterTempLoading(true);
        dropdownFilterRef.current = null;
        applyFilterMutate({ variables: tempApplyFilterVariable });
    }

    //function which finds intersection between new filter and existing filter
    function getIntersectionOnApplyFilter(filter: any, payload: any) {
        const tempApplyFilterResponse = JSON.parse(JSON.stringify(filter));
        let commonIdsDim1 = [];
        let commonIdsDim2 = [];
        if (payload?.dim1?.ids?.length > 0) {
            commonIdsDim1 = [...payload.dim1.ids];
            if (filter.dim1.ids?.length > 0) {
                //if no id in global filter , it means all are selected
                commonIdsDim1 = _.intersection(filter.dim1.ids, payload.dim1.ids);
            }
            tempApplyFilterResponse.dim1.ids = [...commonIdsDim1];
        }
        if (payload?.dim2?.ids?.length > 0) {
            commonIdsDim2 = [...payload.dim2.ids];
            if (filter.dim2.ids?.length > 0) {
                //if no id in global filter , it means all are selected
                commonIdsDim2 = _.intersection(filter.dim2.ids, payload.dim2.ids);
            }
            tempApplyFilterResponse.dim2.ids = [...commonIdsDim2];
        }
        return tempApplyFilterResponse;
    }

    function prepareVariablesAndFetchDropdownData(loading: boolean, offset: number, search: string = '') {
        if (loading) return;
        fetchDropdownData({
            variables: {
                dimension: selectPPG?.dimension,
                attributeType: _.snakeCase(selectPPG?.label),
                limit: globalFilterConfig?.limit,
                filters: JSON.stringify(idForRestOfComponent),
                frequency: currentFrequency?.key,
                offset: offset,
                timePeriod: timePeriodSelection?.toLowerCase(),
                searchKeyword: search,
            },
        });
        setApplyFilterTempLoading(true);
    }

    useEffect(() => {
        if (
            allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !allDimAndAttributeTypeList['loading'] &&
            allDimAndAttributeTypeList['dimentions']
        ) {
            let data = parseAttributeType(allDimAndAttributeTypeList['dimentions'], excludedDimensions);
            if (data.length) {
                data = data?.filter((item: any) => item.dimension === 'dim1');
                setAttributeTypesList(data);
                setSelectPPG(data.find((item: any) => item.label === defaultDimension) || data[0]);
            }
        }
        return () => {
            setSelectPPG(null);
        };
    }, [allDimAndAttributeTypeList?.loading]);

    useEffect(() => {
        if (selectPPG && batchSize) {
            clearGraphData();
            clearTableData();
            setSearch('');
            dropdownOffsetRef.current = 0;
            prepareVariablesAndFetchDropdownData(dropdownLoading, dropdownOffsetRef.current, '');
        }
    }, [selectPPG, idForRestOfComponent]);

    //custom range query
    useEffect(() => {
        if (dropdownFilterRef.current) {
            fetchRangeData();
        }
    }, [timePeriodSelection, selectedPriceTypeDropdown]);

    useEffect(() => {
        if (priceRange && defaultPriceRange) {
            setPriceRange(defaultPriceRange?.[subTileState?.key]);
            setDefaultPriceRange({ ...defaultPriceRange }); //updating state with same state to cause a re render
        }
    }, [subTileState]);

    useEffect(() => {
        if (selectPPG && batchSize && defaultPriceRange) {
            fetchGraphData();
            fetchPricePointTableData();
        }
    }, [yAxisDropdownData, defaultPriceRange, selectedPriceTypeDropdown]);

    useEffect(() => {
        if (selectedGraphItem) {
            drilldownOffsetRef.current = 0;
            fetchPricePointDrilldownTable({
                variables: {
                    attributeType: _.snakeCase(selectPPG?.label),
                    metricType: subTileState?.key,
                    yAxisValue: yAxisDropdownData?.value,
                    frequency: currentFrequency?.key,
                    timePeriod: timePeriodSelection?.toLowerCase(),
                    filters: JSON.stringify(dropdownFilterRef.current),
                    pricePoints: selectedGraphItem?.range,
                    priceType: selectedPriceTypeDropdown?.value,
                },
            });
        }
    }, [selectedGraphItem]);

    function onCustomThresholdApplyButton() {
        fetchPricePointTableData();
        fetchGraphData();
    }

    function fetchRangeData() {
        fetchCustomRangeData({
            variables: {
                filters: JSON.stringify(dropdownFilterRef.current),
                timePeriod: timePeriodSelection?.toLowerCase(),
                frequency: currentFrequency?.key,
                priceType: selectedPriceTypeDropdown?.value,
            },
        });
    }

    function fetchGraphData() {
        setSelectedGraphItem(null);
        setExpandedIndex(null);
        let variables: any = {
            attributeType: _.snakeCase(selectPPG?.label),
            metricType: subTileState?.key,
            yAxisValue: yAxisDropdownData?.value,
            batchSize: Number(batchSize),
            priceRange: priceRange,
            frequency: currentFrequency?.key,
            timePeriod: timePeriodSelection?.toLowerCase(),
            filters: JSON.stringify(dropdownFilterRef.current),
            customThresholds: customThresholdSwitch,
            priceType: selectedPriceTypeDropdown?.value,
        };
        if (customThresholdSwitch) {
            const temp = customThreshold
                .filter((item) => {
                    return item?.value !== '' && !isNaN(Number(item?.value));
                })
                .map((item) => Number(item?.value));
            variables.customThresholdsLst = temp;
        }
        fetchPricePointGraph({
            variables: variables,
        });
    }

    function fetchPricePointTableData() {
        let variables: any = {
            attributeType: _.snakeCase(selectPPG?.label),
            metricType: subTileState?.key,
            yAxisValue: yAxisDropdownData?.value,
            batchSize: Number(batchSize),
            priceRange: priceRange,
            frequency: currentFrequency?.key,
            timePeriod: timePeriodSelection?.toLowerCase(),
            filters: JSON.stringify(dropdownFilterRef.current),
            customThresholds: customThresholdSwitch,
            priceType: selectedPriceTypeDropdown?.value,
        };
        if (customThresholdSwitch) {
            const temp = customThreshold
                .filter((item) => {
                    return item?.value !== '' && !isNaN(Number(item?.value));
                })
                .map((item) => Number(item?.value));
            variables.customThresholdsLst = temp;
        }
        fetchPricePointTable({
            variables: variables,
        });
    }

    const memoisedColumns = useMemo(() => {
        if (pricePointTableData?.tableData)
            return getPricePointColumns(
                pricePointTableData?.tableData,
                expandedIndex,
                setExpandedIndex,
                decimalForNonPercentage,
                decimalForPercentage,
            );
        return [];
    }, [pricePointTableData, expandedIndex]);

    const memoisedDrilldownColumns = useMemo(() => {
        if (drilldownTableData?.tableData)
            return getPricePointDrilldownColumns(
                drilldownTableData?.tableData,
                yAxisDropdownData,
                decimalForNonPercentage,
                decimalForPercentage,
            );
        return [];
    }, [drilldownTableData, expandedIndex]);

    const commonLoading = applyFilterLoading || allDimAndAttributeTypeList?.loading;

    const priceRangeText = `Price Range: ${commonConfig?.units?.currency || '$'}${selectedGraphItem?.range?.[0]} - ${
        commonConfig?.units?.currency || '$'
    }${selectedGraphItem?.range?.[1]}`;

    const drilldownTableExcelData = useMemo(() => {
        return pricePointDrilldownDownload(drilldownTableData?.tableData, selectPPG?.label, priceRangeText);
    }, [drilldownTableData, expandedIndex]);

    const tableExcelData = useMemo(() => {
        return pricePointTableDownload(pricePointTableData?.tableData);
    }, [pricePointTableData?.tableData, expandedIndex]);

    return (
        <section className={`${commonClassName} bg-white`}>
            <h2 className={commonHeaderClass}>{pricePointMainHeader}</h2>
            <div className="flex justify-between my-4">
                <div className="flex items-center gap-4">
                    <CustomSelect
                        disabled={commonLoading || dropdownLoading}
                        className="dropdown-height dropdown-width-alt uniqSelAttributeDropDown"
                        suffixIcon={<DownSvg />}
                        value={selectPPG?.value}
                        options={attributeTypesList}
                        onChange={(val: any, item: any) => {
                            setSelectPPG(item);

                            //mixpanel event
                            trackEventMixpanel(PRICE_POINT_ATTRIBUTE_TYPE_DROPDOWN_CHANGED, {
                                dropdownName: item?.label,
                            });
                        }}
                    />
                    <PricePointMultiSelectionDropdownV2
                        search={search}
                        setSearch={setSearch}
                        setApplyFilterTempLoading={setApplyFilterTempLoading}
                        fetchDropdownData={prepareVariablesAndFetchDropdownData}
                        offset={dropdownOffsetRef}
                        options={dropdownData}
                        loading={dropdownLoading}
                        disabled={applyFilterLoading}
                        count={dropdownCount}
                        onCheckboxChange={onCheckboxChange}
                        attributeType={selectPPG}
                        dropdownValue={selectPPG?.value}
                    />
                </div>
                <div className="flex items-center gap-2 ">
                    <CustomSelect
                        className="dropdown-height dropdown-width uniqSelPriceDropDown"
                        suffixIcon={<DownSvg />}
                        value={selectedPriceTypeDropdown?.value}
                        options={priceTypeOptions}
                        onChange={(val: any, item: any) => {
                            setSelectedPriceTypeDropdown(item);
                            //mixpanel event
                            trackEventMixpanel(PRICE_POINT_PRICE_TYPE_DROPDOWN_CHANGED, {
                                priceTypeName: item?.label,
                            });
                        }}
                        disabled={allDimAndAttributeTypeList?.loading}
                    />

                    <CustomSelect
                        disabled={allDimAndAttributeTypeList?.loading}
                        className="dropdown-height dropdown-width uniqSelValueDropDown"
                        suffixIcon={<DownSvg />}
                        value={yAxisDropdownData?.value}
                        options={pricePointConfig?.yAxisDropdownData}
                        onChange={(val: any, item: any) => {
                            setYAxisDropdownData(item);
                            //mixpanel event
                            trackEventMixpanel(PRICE_POINT_VALUE_DROPDOWN_CHANGED, {
                                dropdownName: item?.label,
                            });
                        }}
                    />
                    <TileButton
                        disabled={allDimAndAttributeTypeList?.loading}
                        onClick={(item: any) => {
                            setSubTileState(item);
                            //mixpanel event
                            trackEventMixpanel(PRICE_POINT_TAB_CHANGED, {
                                tabName: item?.label,
                            });
                        }}
                        activeKey={subTileState.key}
                        data={pricePointConfig.subTileData}
                    />
                </div>
            </div>

            <PricePointSection
                defaultPriceRange={defaultPriceRange}
                customThresholdSwitch={customThresholdSwitch}
                setCustomThresholdSwitch={setCustomThresholdSwitch}
                tiles={customThreshold}
                setTiles={setCustomThreshold}
                fetchPricePointGraph={onCustomThresholdApplyButton}
                batchSize={batchSize}
                setBatchSize={setBatchSize}
                priceRange={priceRange}
                setPriceRange={setPriceRange}
                data={graphData}
                error={graphError}
                loading={
                    commonLoading ||
                    graphLoading ||
                    customRangeLoading ||
                    (dropdownLoading && !search && dropdownOffsetRef.current === 0)
                }
                setSelectedGraphItem={setSelectedGraphItem}
                selectedGraphItem={selectedGraphItem}
                selectPPG={selectPPG}
                subTileState={subTileState}
                yAxisDropdownData={yAxisDropdownData}
                priceType={selectedPriceTypeDropdown?.label?.split(' ')[0]}
            />
            {selectedGraphItem && (
                <div ref={expandedTableCopyRef} className="pt-4">
                    <div className="mb-4 flex justify-between items-center">
                        <div className={`${subHeaderClass}`}>{priceRangeText}</div>
                        {!(commonLoading || drilldownTableDataLoading) && (
                            <div className="flex items-center justify-center gap-2">
                                <CopyToClipBoard refOfComponent={expandedTableCopyRef} />
                                <DownloadExcel
                                    title={'PricePointDrilldownTable'}
                                    excelData={drilldownTableExcelData.data}
                                    headers={drilldownTableExcelData.headers}
                                />
                            </div>
                        )}
                    </div>

                    <CustomTable
                        columns={memoisedDrilldownColumns}
                        pagination={false}
                        scroll={{ x: 'auto', y: height + 100 }}
                        loading={
                            commonLoading ||
                            drilldownTableDataLoading ||
                            (dropdownLoading && !search && dropdownOffsetRef.current === 0)
                        }
                        dataSource={drilldownTableData?.tableData}
                    />
                </div>
            )}
            <div ref={tableCopyRef} className="py-4" id={tableId}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className={`${subHeaderClass} mb-0`}>KPIs across Price Point</h2>
                    <div className="flex justify-end gap-2 items-center">
                        {!(commonLoading || pricePointTableLoading) && (
                            <>
                                <CopyToClipBoard refOfComponent={tableCopyRef} />
                                <DownloadExcel
                                    title={'PricePointTable'}
                                    excelData={tableExcelData.data}
                                    headers={tableExcelData.headers}
                                />
                            </>
                        )}
                    </div>
                </div>

                <CustomTableExpanded
                    columns={memoisedColumns}
                    pagination={false}
                    scroll={{ x: '100%', y: height + 100 }}
                    loading={
                        commonLoading ||
                        pricePointTableLoading ||
                        customRangeLoading ||
                        (dropdownLoading && !search && dropdownOffsetRef.current === 0)
                    }
                    dataSource={pricePointTableData?.tableData}
                />
            </div>
        </section>
    );
};

export default PricePoint;
